import {Injectable, InjectionToken, Injector} from '@angular/core';
import moment from 'moment';
import {defer, merge, NEVER, Observable, of, Subject} from 'rxjs';
import {distinctUntilChanged, share, takeUntil} from 'rxjs/operators';
import {toNever} from '../../common/rxjs/to-never';
import {destroy$} from '../destroyable';
import {StreamingService} from '../streaming/streaming.service';
import {GenericSystemStatusLogger, GenericSystemStatusLogRec} from './system-status-logger';

export const SYSTEM_STATUS_STREAMING_SERVICE_TOKEN = new InjectionToken<StreamingService>(
    'systemStatusStreamingService'
);

interface FailRec {
    logRec: GenericSystemStatusLogRec;
    timestamp: number;
    isoTime: string;
}

@Injectable()
export class SystemStatusService implements GenericSystemStatusLogger {

    public readonly isOk$: Observable<boolean>;

    public isOk: boolean = true;

    public readonly keepStatusStreamConnection$: Observable<never>;

    public readonly updated$: Observable<void>;

    public readonly destroy$ = destroy$();

    private readonly fails: FailRec[] = [];

    private readonly isOkSubject$ = new Subject<boolean>();

    private readonly updatedSubject$ = new Subject<void>();

    constructor(
        private injector: Injector
    ) {
        this.isOk$ = merge(
            defer(() => of(this.isOk)),
            this.isOkSubject$
        ).pipe(
            distinctUntilChanged()
        );

        this.keepStatusStreamConnection$ = this.getStatusStreamConnectionStream();
        this.updated$ = this.updatedSubject$.asObservable();
    }

    public getFailsEncodedMsg(): string {
        return [...this.fails]
            .sort((failRec1, failRec2) => failRec1.timestamp - failRec2.timestamp)
            .map(failRec => [
                moment(failRec.timestamp).set({year: 1970}).unix(),
                failRec.logRec.codes.join(',')
            ].join(','))
            .join(';');
    }

    public logStreamingStatus(logRec: GenericSystemStatusLogRec): void {
        const knownFailRecIndex = this.fails.findIndex(failRec => failRec.logRec.key === logRec.key);

        if (logRec.isOk) {
            if (knownFailRecIndex >= 0) {
                this.fails.splice(knownFailRecIndex, 1);
            }
        } else {
            const momentTime = moment();

            const failRec: FailRec = {
                logRec,
                timestamp: momentTime.valueOf(),
                isoTime: momentTime.toISOString(true)
            };

            if (knownFailRecIndex >= 0) {
                this.fails[knownFailRecIndex] = failRec;
            } else {
                this.fails.push(failRec);
            }
        }

        this.updatedSubject$.next();
        this.updateIsOk();
    }

    private updateIsOk(): void {
        const isOk = !this.fails.length;

        if (isOk !== this.isOk) {
            this.isOk = isOk;
            this.isOkSubject$.next(isOk);
        }
    }

    private getStatusStreamConnectionStream(): Observable<never> {
        return defer(() => {

            // Inject the service here to break cyclic dependency.
            const systemStatusStreamingService = this.injector.get(SYSTEM_STATUS_STREAMING_SERVICE_TOKEN, null);

            return systemStatusStreamingService
                ? systemStatusStreamingService.ready$.pipe(
                    toNever()
                )
                : NEVER;
        }).pipe(
            takeUntil(this.destroy$),
            share()
        );
    }

}
